import React from 'react';
import { ShieldCheckIcon, HeartIcon, AwardIcon, UsersIcon, PlayCircleIcon, MapPinIcon } from 'lucide-react';
export function TrustSection() {
  return <section id="why-us" className="w-full bg-soft-teal py-16 lg:py-24" aria-label="Proudly Canadian and Nurse-Owned trust section" data-id="element-726">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-727">
        {/* Section header */}
        <div className="text-center mb-14" data-id="element-728">
          <div className="inline-flex items-center gap-2 bg-white text-medical-blue text-xs font-semibold px-3 py-1.5 rounded-full mb-4 shadow-sm" data-id="element-729">
            🍁 Canadian Owned & Operated
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-text-dark tracking-tight mb-4" data-id="element-730">
            Proudly Canadian &{' '}
            <span className="text-medical-blue" data-id="element-731">Nurse-Owned</span>
          </h2>
          <p className="text-text-muted text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-732">
            Built by nurses, for nurses. Care Nomad was founded by a registered
            nurse who experienced firsthand the broken staffing system — and
            decided to fix it.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-16" data-id="element-733">
          {/* Left: Founder video bio */}
          <div className="relative" data-id="element-734">
            <div className="bg-white rounded-2xl shadow-float overflow-hidden" data-id="element-735">
              {/* Video placeholder */}
              <div className="relative bg-gradient-to-br from-medical-blue to-medical-blue-dark aspect-video flex items-center justify-center group cursor-pointer" data-id="element-736">
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-10" data-id="element-737">
                  <div className="absolute top-4 left-4 w-32 h-32 border-2 border-white rounded-full" data-id="element-738" />
                  <div className="absolute bottom-4 right-4 w-24 h-24 border-2 border-white rounded-full" data-id="element-739" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 border border-white rounded-full" data-id="element-740" />
                </div>

                {/* Founder avatar placeholder */}
                <div className="relative z-10 text-center" data-id="element-741">
                  <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 border-2 border-white/40" data-id="element-742">
                    <span className="text-white font-bold text-2xl" data-id="element-743">EU</span>
                  </div>
                  <div className="text-white font-bold text-lg" data-id="element-744">Ese Uko</div>
                  <div className="text-white/70 text-sm" data-id="element-745">
                    Co-Founder & Chief Recruitment Officer, Care Nomad
                  </div>
                </div>

                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors" data-id="element-746">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform" data-id="element-747">
                    <PlayCircleIcon className="w-8 h-8 text-medical-blue ml-0.5" data-id="element-748" />
                  </div>
                </div>

                {/* Duration badge */}
                <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-lg font-medium" data-id="element-749">
                  3:42
                </div>
              </div>

              {/* Founder quote */}
              <div className="p-6" data-id="element-750">
                <blockquote className="text-text-dark leading-relaxed italic mb-4" data-id="element-751">
                  "I spent 12 years as a bedside nurse watching my colleagues
                  burn out because agencies treated them like commodities. Care
                  Nomad gives healthcare workers the dignity, flexibility, and
                  pay they deserve — while giving facilities the reliable staff
                  they need."
                </blockquote>
                <div className="flex items-center gap-3" data-id="element-752">
                  <div className="w-10 h-10 bg-soft-teal rounded-full flex items-center justify-center font-bold text-medical-blue text-sm" data-id="element-753">
                    EU
                  </div>
                  <div data-id="element-754">
                    <div className="font-bold text-text-dark text-sm" data-id="element-755">
                      Ese Uko
                    </div>
                    <div className="text-text-muted text-xs" data-id="element-756">
                      Co-Founder · Chief Recruitment Officer
                    </div>
                  </div>
                  <div className="ml-auto" data-id="element-757">
                    <div className="flex items-center gap-1 text-xs text-text-muted" data-id="element-758">
                      <MapPinIcon className="w-3 h-3" data-id="element-759" />
                      Toronto, ON
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Trust pillars */}
          <div className="space-y-5" data-id="element-760">
            <div className="bg-white rounded-2xl p-6 shadow-card border border-gray-100" data-id="element-761">
              <div className="flex items-start gap-4" data-id="element-762">
                <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-763">
                  <ShieldCheckIcon className="w-6 h-6 text-accent-teal" data-id="element-764" />
                </div>
                <div data-id="element-765">
                  <h3 className="font-bold text-text-dark mb-1" data-id="element-766">
                    Nurse-Vetted Quality Standard
                  </h3>
                  <p className="text-text-muted text-sm leading-relaxed" data-id="element-767">
                    Every worker is screened by a registered nurse — not an HR
                    generalist. We verify credentials, check references, and
                    assess clinical competency before anyone joins the platform.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-card border border-gray-100" data-id="element-768">
              <div className="flex items-start gap-4" data-id="element-769">
                <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-770">
                  <HeartIcon className="w-6 h-6 text-accent-teal" data-id="element-771" />
                </div>
                <div data-id="element-772">
                  <h3 className="font-bold text-text-dark mb-1" data-id="element-773">
                    Worker-First Philosophy
                  </h3>
                  <p className="text-text-muted text-sm leading-relaxed" data-id="element-774">
                    Healthcare workers choose their shifts, set their rates, and
                    keep more of what they earn. Better pay and flexibility
                    means better retention and better patient outcomes.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-card border border-gray-100" data-id="element-775">
              <div className="flex items-start gap-4" data-id="element-776">
                <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-777">
                  <AwardIcon className="w-6 h-6 text-accent-teal" data-id="element-778" />
                </div>
                <div data-id="element-779">
                  <h3 className="font-bold text-text-dark mb-1" data-id="element-780">
                    Regulated & Compliant
                  </h3>
                  <p className="text-text-muted text-sm leading-relaxed" data-id="element-781">
                    Fully compliant with Ontario's LTCHA, BC's CCALA, and
                    federal healthcare regulations. All workers carry valid
                    CNO/CARNA registration and liability insurance.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-card border border-gray-100" data-id="element-782">
              <div className="flex items-start gap-4" data-id="element-783">
                <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-784">
                  <UsersIcon className="w-6 h-6 text-accent-teal" data-id="element-785" />
                </div>
                <div data-id="element-786">
                  <h3 className="font-bold text-text-dark mb-1" data-id="element-787">
                    Community-Driven Growth
                  </h3>
                  <p className="text-text-muted text-sm leading-relaxed" data-id="element-788">
                    A portion of every transaction funds nursing scholarships
                    and mental health support for healthcare workers. When
                    nurses thrive, communities thrive.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Certifications & logos bar */}
        <div className="bg-white rounded-2xl p-6 shadow-card border border-gray-100" data-id="element-789">
          <div className="text-center text-xs font-semibold text-text-muted uppercase tracking-widest mb-6" data-id="element-790">
            Recognized & Regulated By
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6" data-id="element-791">
            <div className="text-center" data-id="element-792">
              <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center mx-auto mb-2" data-id="element-793">
                <span className="text-medical-blue font-bold text-xs" data-id="element-794">CNO</span>
              </div>
              <div className="text-xs text-text-muted font-medium" data-id="element-795">
                College of Nurses of Ontario
              </div>
            </div>
            <div className="text-center" data-id="element-796">
              <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center mx-auto mb-2" data-id="element-797">
                <span className="text-medical-blue font-bold text-xs" data-id="element-798">
                  CARNA
                </span>
              </div>
              <div className="text-xs text-text-muted font-medium" data-id="element-799">
                College & Association of RNs of Alberta
              </div>
            </div>
            <div className="text-center" data-id="element-800">
              <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center mx-auto mb-2" data-id="element-801">
                <span className="text-medical-blue font-bold text-xs" data-id="element-802">
                  BCCNM
                </span>
              </div>
              <div className="text-xs text-text-muted font-medium" data-id="element-803">
                BC College of Nurses & Midwives
              </div>
            </div>
            <div className="text-center" data-id="element-804">
              <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center mx-auto mb-2" data-id="element-805">
                <span className="text-medical-blue font-bold text-xs" data-id="element-806">🍁</span>
              </div>
              <div className="text-xs text-text-muted font-medium" data-id="element-807">
                Canadian Owned & Operated
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}